import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import styled from 'styled-components';
import { venposService } from '../../../../services/venposService';
import { useBooking, BOOKING_ACTIONS } from '../../../../contexts/BookingContext';

const isJune212026VisitDate = (date) => {
  if (!date) return false;
  const d = date instanceof Date ? date : new Date(date);
  if (Number.isNaN(d.getTime())) return false;
  return d.getFullYear() === 2026 && d.getMonth() === 5 && d.getDate() === 21;
};

const isAviaryAfternoonTeaUpsell = (upsellName) => {
  const n = (upsellName || '').toLowerCase();
  return n.includes('aviary tearoom') && n.includes('afternoon tea');
};

const isGentlemansAfternoonTeaProduct = (itemName) => {
  const n = (itemName || '').toLowerCase();
  return n.includes('gentleman') && n.includes('afternoon tea');
};

const shouldShowAfternoonTeaProduct = (upsellName, itemName, visitDate) => {
  const ups = (upsellName || '').toLowerCase();
  const item = (itemName || '').toLowerCase();
  if (ups.includes('aviary tearoom') && ups.includes('afternoon tea')) {
    if (item.includes('gentleman') && item.includes('afternoon tea')) {
      return isJune212026VisitDate(visitDate);
    }
  }
  return true;
};

const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.6);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 99999 !important;
  padding: 1rem;
`;

const ModalContainer = styled.div`
  background: white;
  border-radius: 16px;
  max-width: 800px;
  width: 100%;
  max-height: 90vh;
  overflow-y: auto;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
  animation: modalSlideIn 0.3s ease-out;
  
  @keyframes modalSlideIn {
    from {
      opacity: 0;
      transform: scale(0.9) translateY(-20px);
    }
    to {
      opacity: 1;
      transform: scale(1) translateY(0);
    }
  }
`;

const ModalHeader = styled.div`
  padding: 2rem 2rem 1rem;
  border-bottom: 1px solid ${props => props.theme.colors.greyBackground};
  position: relative;
`;

const CloseButton = styled.button`
  position: absolute;
  top: 1.5rem;
  right: 1.5rem;
  width: 40px;
  height: 40px;
  border: none;
  background: ${props => props.theme.colors.greyBackground};
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  font-size: 1.5rem;
  color: ${props => props.theme.colors.secondary};
  transition: all 0.2s ease;
  
  &:hover {
    background: ${props => props.theme.colors.primary};
    color: white;
    transform: scale(1.1);
  }
`;

const ModalTitle = styled.h2`
  color: ${props => props.theme.colors.primary};
  font-size: 1.75rem;
  font-weight: 600;
  margin-bottom: 0.5rem;
  padding-right: 3rem;
`;

const ModalDescription = styled.p`
  color: ${props => props.theme.colors.secondary};
  font-size: 1.1rem;
  line-height: 1.5;
  margin-bottom: 0;
`;

const ModalContent = styled.div`
  padding: 1.5rem 2rem 2rem;
`;

const ItemsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: 1.5rem;
  margin-bottom: 2rem;
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const ItemCard = styled.div`
  background: ${props => props.isSelected ? props.theme.colors.offWhite : 'white'};
  border: 2px solid ${props => props.isSelected ? props.theme.colors.accent : props.theme.colors.greyBackground};
  border-radius: 12px;
  padding: 1.5rem;
  cursor: pointer;
  transition: all 0.3s ease;
  position: relative;
  
  &:hover {
    border-color: ${props => props.theme.colors.primary};
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  }
  
  ${props => props.isSelected && `
    box-shadow: 0 4px 15px rgba(0, 0, 0, 0.1);
  `}
`;

const ItemName = styled.h3`
  color: ${props => props.theme.colors.primary};
  font-size: 1.2rem;
  font-weight: 600;
  margin-bottom: 0.75rem;
`;

const ItemDescription = styled.p`
  color: ${props => props.theme.colors.secondary};
  font-size: 0.95rem;
  line-height: 1.4;
  margin-bottom: 1rem;
`;

const FathersDaySpecialNote = styled.div`
  font-size: 0.875rem;
  font-weight: 600;
  color: ${props => props.theme.colors.accent};
  margin-bottom: 0.5rem;
`;

const ItemPrice = styled.div`
  font-size: 1.3rem;
  font-weight: 700;
  color: ${props => props.theme.colors.accent};
  margin-bottom: 1rem;
`;

const QuantitySelector = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  margin-top: 1rem;
`;

const QuantityButton = styled.button`
  width: 36px;
  height: 36px;
  border: 2px solid ${props => props.theme.colors.greyBackground};
  background: white;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  font-size: 1.1rem;
  font-weight: 600;
  color: ${props => props.theme.colors.primary};
  transition: all 0.2s ease;
  
  &:hover {
    border-color: ${props => props.theme.colors.primary};
    background: ${props => props.theme.colors.offWhite};
    transform: scale(1.05);
  }
  
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
    transform: none;
  }
`;

const QuantityInput = styled.input`
  width: 60px;
  text-align: center;
  border: 2px solid ${props => props.theme.colors.greyBackground};
  border-radius: 8px;
  padding: 0.5rem;
  font-size: 1rem;
  font-weight: 600;
  
  &:focus {
    outline: none;
    border-color: ${props => props.theme.colors.primary};
    box-shadow: 0 0 0 3px ${props => props.theme.colors.primary}20;
  }
`;

const SelectedBadge = styled.div`
  position: absolute;
  top: -8px;
  right: -8px;
  background: ${props => props.theme.colors.accent};
  color: white;
  border-radius: 50%;
  width: 24px;
  height: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.8rem;
  font-weight: 600;
`;

const ModalFooter = styled.div`
  padding: 1.5rem 2rem;
  border-top: 1px solid ${props => props.theme.colors.greyBackground};
  display: flex;
  justify-content: space-between;
  align-items: center;
  background: ${props => props.theme.colors.offWhite};
  border-radius: 0 0 16px 16px;
`;

const TotalAmount = styled.div`
  font-size: 1.2rem;
  font-weight: 600;
  color: ${props => props.theme.colors.primary};
`;

const ActionButtons = styled.div`
  display: flex;
  gap: 1rem;
`;

const Button = styled.button`
  padding: 0.75rem 1.5rem;
  border-radius: 8px;
  font-size: 1rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  border: 2px solid transparent;
`;

const CancelButton = styled(Button)`
  background: transparent;
  color: ${props => props.theme.colors.secondary};
  border-color: ${props => props.theme.colors.greyBackground};
  
  &:hover {
    background: ${props => props.theme.colors.greyBackground};
    color: ${props => props.theme.colors.primary};
  }
`;

const AddToBasketButton = styled(Button)`
  background: ${props => props.theme.colors.accent};
  color: white;
  border-color: ${props => props.theme.colors.accent};
  
  &:hover {
    background: ${props => props.theme.colors.primary};
    border-color: ${props => props.theme.colors.primary};
    transform: translateY(-1px);
  }
  
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
    transform: none;
  }
`;

const LoadingSpinner = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 3rem;
  color: ${props => props.theme.colors.secondary};
  font-size: 1.1rem;
  
  &::before {
    content: '⟳';
    margin-right: 0.5rem;
    animation: spin 1s linear infinite;
  }
  
  @keyframes spin {
    from { transform: rotate(0deg); }
    to { transform: rotate(360deg); }
  }
`;

const TimeSelectionContainer = styled.div`
  margin-top: 1.5rem;
`;

const TimeGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 1rem;
  margin-bottom: 1.5rem;
`;

const TimeOption = styled.div`
  background: ${props => props.disabled ? '#f5f5f5' : props.isSelected ? props.theme.colors.offWhite : 'white'};
  border: 2px solid ${props => props.disabled ? '#e0e0e0' : props.isSelected ? props.theme.colors.accent : props.theme.colors.greyBackground};
  border-radius: 12px;
  padding: 1.25rem;
  cursor: ${props => props.disabled ? 'not-allowed' : 'pointer'};
  transition: all 0.3s ease;
  text-align: center;
  opacity: ${props => props.disabled ? 0.5 : 1};
  
  &:hover {
    border-color: ${props => props.disabled ? '#e0e0e0' : props.theme.colors.primary};
    transform: ${props => props.disabled ? 'none' : 'translateY(-2px)'};
    box-shadow: ${props => props.disabled ? 'none' : '0 4px 12px rgba(0, 0, 0, 0.1)'};
  }
  
  ${props => props.isSelected && `
    box-shadow: 0 4px 15px rgba(0, 0, 0, 0.1);
  `}
`;

const TimeDisplay = styled.div`
  font-size: 1.1rem;
  font-weight: 600;
  color: ${props => props.theme.colors.primary};
  margin-bottom: 0.5rem;
`;

const TimeDescription = styled.div`
  font-size: 0.9rem;
  color: ${props => props.theme.colors.secondary};
  margin-bottom: 0.5rem;
`;

const TimeAvailability = styled.div`
  font-size: 0.85rem;
  color: ${props => props.theme.colors.secondary};
  font-weight: 500;
`;

const BackButton = styled(Button)`
  background: transparent;
  color: ${props => props.theme.colors.secondary};
  border-color: ${props => props.theme.colors.greyBackground};
  
  &:hover {
    background: ${props => props.theme.colors.greyBackground};
    color: ${props => props.theme.colors.primary};
  }
`;

const QuestionsContainer = styled.div`
  margin-top: 1.5rem;
`;

const QuestionItem = styled.div`
  background: white;
  border: 2px solid ${props => props.theme.colors.greyBackground};
  border-radius: 12px;
  padding: 1.5rem;
  margin-bottom: 1.5rem;
`;

const QuestionText = styled.div`
  font-size: 1.1rem;
  font-weight: 600;
  color: ${props => props.theme.colors.primary};
  margin-bottom: 1rem;
`;

const QuestionInput = styled.input`
  width: 100%;
  padding: 0.75rem;
  border: 2px solid ${props => props.theme.colors.greyBackground};
  border-radius: 8px;
  font-size: 1rem;
  transition: border-color 0.2s ease;
  
  &:focus {
    outline: none;
    border-color: ${props => props.theme.colors.primary};
    box-shadow: 0 0 0 3px ${props => props.theme.colors.primary}20;
  }
`;

const QuestionTextArea = styled.textarea`
  width: 100%;
  padding: 0.75rem;
  border: 2px solid ${props => props.theme.colors.greyBackground};
  border-radius: 8px;
  font-size: 1rem;
  min-height: 80px;
  resize: vertical;
  font-family: inherit;
  transition: border-color 0.2s ease;
  
  &:focus {
    outline: none;
    border-color: ${props => props.theme.colors.primary};
    box-shadow: 0 0 0 3px ${props => props.theme.colors.primary}20;
  }
`;

const RequiredIndicator = styled.span`
  color: ${props => props.theme.colors.accent};
  margin-left: 0.25rem;
`;

const StepIndicator = styled.div`
  display: flex;
  justify-content: center;
  margin-bottom: 2rem;
  gap: 1rem;
`;

const StepDot = styled.div`
  width: 12px;
  height: 12px;
  border-radius: 50%;
  background: ${props => props.isActive ? props.theme.colors.primary : props.theme.colors.greyBackground};
  transition: all 0.3s ease;
`;

const ModifiersContainer = styled.div`
  margin-top: 1.5rem;
`;

const ModifierGroup = styled.div`
  background: white;
  border: 2px solid ${props => props.theme.colors.greyBackground};
  border-radius: 12px;
  padding: 1.5rem;
  margin-bottom: 1.5rem;
`;

const ModifierPrompt = styled.div`
  font-size: 1.1rem;
  font-weight: 600;
  color: ${props => props.theme.colors.primary};
  margin-bottom: 1rem;
`;

const ModifierOptions = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 1rem;
`;

const ModifierOption = styled.div`
  background: ${props => props.isSelected ? props.theme.colors.offWhite : 'white'};
  border: 2px solid ${props => props.isSelected ? props.theme.colors.accent : props.theme.colors.greyBackground};
  border-radius: 8px;
  padding: 1rem;
  cursor: pointer;
  transition: all 0.3s ease;
  text-align: center;
  
  &:hover {
    border-color: ${props => props.theme.colors.primary};
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  }
  
  ${props => props.isSelected && `
    box-shadow: 0 4px 15px rgba(0, 0, 0, 0.1);
  `}
`;

const ModifierName = styled.div`
  font-size: 1rem;
  font-weight: 600;
  color: ${props => props.theme.colors.primary};
  margin-bottom: 0.25rem;
`;

const ModifierPrice = styled.div`
  font-size: 0.9rem;
  color: ${props => props.theme.colors.secondary};
  font-weight: 500;
`;

const UpsellDetailModal = ({ 
  isOpen, 
  onClose, 
  upsell, 
  selectedDate,
  locationId,
  pageId,
  onAddToBasket,
  loading = false 
}) => {
  const { modifierSelections, selectedTimeSlot, dispatch } = useBooking();
  
  const [itemDetails, setItemDetails] = useState(null);
  const [selectedItems, setSelectedItems] = useState({});
  const [loadingDetails, setLoadingDetails] = useState(false);
  const [eventTimes, setEventTimes] = useState(null);
  const [selectedEventTime, setSelectedEventTime] = useState(null);
  const [loadingTimes, setLoadingTimes] = useState(false);
  const [eventQuestions, setEventQuestions] = useState(null);
  const [questionAnswers, setQuestionAnswers] = useState({});
  const [loadingQuestions, setLoadingQuestions] = useState(false);
  const [productModifiers, setProductModifiers] = useState(null);
  const [localModifierSelections, setLocalModifierSelections] = useState({});
  const [loadingModifiers, setLoadingModifiers] = useState(false);
  const [currentStep, setCurrentStep] = useState('items'); // 'items', 'times', 'questions', or 'modifiers'
  
  // Ref for modal container to enable auto-scrolling
  const modalContentRef = useRef(null);

  // Auto-scroll when time is selected
  useEffect(() => {
    if (selectedEventTime && modalContentRef.current) {
      // Scroll to bottom of modal smoothly after a short delay
      setTimeout(() => {
        if (modalContentRef.current) {
          modalContentRef.current.scrollTo({
            top: modalContentRef.current.scrollHeight,
            behavior: 'smooth'
          });
        }
      }, 100); // Small delay to let content render
    }
  }, [selectedEventTime]);

  // Reset state when modal opens/closes
  useEffect(() => {
    if (isOpen) {
      setSelectedItems({});
      setItemDetails(null);
      setEventTimes(null);
      setSelectedEventTime(null);
      setEventQuestions(null);
      setQuestionAnswers({});
      setProductModifiers(null);
      setLocalModifierSelections({});
      setCurrentStep('items');
    }
  }, [isOpen]);

  // Load item details when modal opens
  useEffect(() => {
    const loadItemDetails = async () => {
      if (isOpen && upsell && !itemDetails) {
        setLoadingDetails(true);
        try {
          const response = await venposService.getUpsellItemDetails(
            '978', // locationId
            upsell.id,
            upsell.pageType || 'EventTemplate',
            'OnScreenCalendar',
            selectedDate?.toISOString().split('T')[0] || ''
          );
          
          if (response?.data) {
            setItemDetails(response.data);
          }
        } catch (error) {

        } finally {
          setLoadingDetails(false);
        }
      }
    };

    loadItemDetails();
  }, [isOpen, upsell, itemDetails, selectedDate]);

  const handleItemQuantityChange = (itemId, quantity) => {
    const newQuantity = Math.max(0, Math.min(quantity, 10));
    
    setSelectedItems(prev => ({
      ...prev,
      [itemId]: newQuantity
    }));
  };

  const incrementQuantity = (itemId) => {
    const currentQuantity = selectedItems[itemId] || 0;
    handleItemQuantityChange(itemId, currentQuantity + 1);
  };

  const decrementQuantity = (itemId) => {
    const currentQuantity = selectedItems[itemId] || 0;
    handleItemQuantityChange(itemId, currentQuantity - 1);
  };

  const handleQuantityInputChange = (itemId, value) => {
    const quantity = parseInt(value) || 0;
    handleItemQuantityChange(itemId, quantity);
  };

  const calculateTotal = () => {
    if (!itemDetails?.webSaleItems) return 0;
    
    return itemDetails.webSaleItems.reduce((total, item) => {
      const quantity = selectedItems[item.webSaleItemId] || 0;
      return total + (item.price * quantity);
    }, 0);
  };

  const loadEventTimes = async (selectedItemsList) => {
    if (!upsell || !selectedDate) return;

    setLoadingTimes(true);
    try {
      // Prepare items for the API call
      const items = selectedItemsList.map(item => ({
        pageType: 'WebPage',
        pageId: upsell.parentWebSaleItemId || 'f7be8040-b3de-43e8-a064-b173b1a8d761',
        webSaleItemId: item.webSaleItemId,
        quantity: item.quantity,
        webSaleItemTypeID: item.webSaleItemTypeID,
        name: item.name,
        selectedDate: selectedDate.toISOString().split('T')[0] + 'T10:00:00',
        reservationExpiryDateTime: new Date(Date.now() + 30 * 60 * 1000).toISOString(),
        eventCapacityUsageId: null,
        eventId: null,
        fixedDonationAmount: 0,
        giftAidEligible: false,
        isSysGift: false,
        isSysMemberCard: false
      }));

      const response = await venposService.getUpsellEventTimes(
        String(locationId),
        String(pageId),
        upsell.id,
        upsell.pageType || 'EventTemplate',
        selectedDate.toISOString().split('T')[0] + 'T10:00:00',
        items
      );

      if (response?.data?.events) {
        setEventTimes(response.data);
        setCurrentStep('times');
      }
    } catch (error) {

      // Fallback: proceed without time selection
      handleAddToBasket();
    } finally {
      setLoadingTimes(false);
    }
  };

  const handleAddToBasket = () => {
    const itemsToAdd = Object.entries(selectedItems)
      .filter(([_, quantity]) => quantity > 0)
      .map(([itemId, quantity]) => {
        const item = itemDetails.webSaleItems.find(i => i.webSaleItemId === itemId);
        const modifierSelection = localModifierSelections[itemId];
        
        return {
          ...item,
          quantity,
          selectedDate: selectedDate?.toISOString().split('T')[0] || '',
          selectedEventTime: selectedEventTime,
          questionAnswers: questionAnswers,
          modifierSelection: modifierSelection,
          // Add upsell context
          parentUpsellId: upsell.id,
          parentUpsellName: upsell.name
        };
      });

    if (itemsToAdd.length > 0) {
      onAddToBasket(itemsToAdd);
      onClose();
    }
  };

  const loadEventQuestions = async () => {
    if (!selectedEventTime) return;

    setLoadingQuestions(true);
    try {
      // Prepare webSaleItems object for the API call
      const webSaleItems = {};
      Object.entries(selectedItems)
        .filter(([_, quantity]) => quantity > 0)
        .forEach(([itemId, quantity]) => {
          webSaleItems[itemId] = quantity;
        });

      // Prepare eventIds array
      const eventIds = [selectedEventTime.eventId];

      const response = await venposService.getEventQuestions(
        '978', // locationId
        webSaleItems,
        eventIds
      );

      if (response?.data?.eventQuestions && response.data.eventQuestions.length > 0) {
        setEventQuestions(response.data);
        setCurrentStep('questions');
      } else {
        // No questions, proceed to modifiers or basket
        loadProductModifiers();
      }
    } catch (error) {

      // Fallback: proceed to modifiers or basket
      loadProductModifiers();
    } finally {
      setLoadingQuestions(false);
    }
  };

  const handleContinueToTimes = () => {
    const selectedItemsList = Object.entries(selectedItems)
      .filter(([_, quantity]) => quantity > 0)
      .map(([itemId, quantity]) => {
        const item = itemDetails.webSaleItems.find(i => i.webSaleItemId === itemId);
        return { ...item, quantity };
      });

    if (selectedItemsList.length > 0) {
      loadEventTimes(selectedItemsList);
    }
  };

  const loadProductModifiers = async () => {
    setLoadingModifiers(true);
    try {
      // Prepare items for the API call
      const items = Object.entries(selectedItems)
        .filter(([_, quantity]) => quantity > 0)
        .map(([itemId, quantity]) => {
          const item = itemDetails.webSaleItems.find(i => i.webSaleItemId === itemId);
          return {
            pageType: 'WebPage',
            pageId: upsell.parentWebSaleItemId || 'f7be8040-b3de-43e8-a064-b173b1a8d761',
            webSaleItemId: item.webSaleItemId,
            quantity: quantity,
            webSaleItemTypeID: item.webSaleItemTypeID,
            name: item.name,
            selectedDate: selectedEventTime?.startDateTime || (selectedDate.toISOString().split('T')[0] + 'T10:00:00'),
            reservationExpiryDateTime: new Date(Date.now() + 30 * 60 * 1000).toISOString(),
            eventCapacityUsageId: selectedEventTime?.eventId || null,
            eventId: selectedEventTime?.eventId || null,
            fixedDonationAmount: 0,
            giftAidEligible: false,
            isSysGift: false,
            isSysMemberCard: false
          };
        });

      const response = await venposService.getProductModifiers(String(locationId), items);

      if (response?.data && response.data.length > 0) {
        setProductModifiers(response.data);
        setCurrentStep('modifiers');
      } else {
        // No modifiers, proceed directly to basket
        handleAddToBasket();
      }
    } catch (error) {

      // Fallback: proceed without modifiers
      handleAddToBasket();
    } finally {
      setLoadingModifiers(false);
    }
  };

  const handleContinueToQuestions = () => {
    if (selectedEventTime) {
      loadEventQuestions();
    }
  };

  const handleContinueToModifiers = () => {
    loadProductModifiers();
  };

  const handleQuestionAnswerChange = (questionId, answer) => {
    setQuestionAnswers(prev => ({
      ...prev,
      [questionId]: answer
    }));
  };

  const handleModifierSelection = async (modifierId, modifierTextId, itemId) => {
    const newSelections = {
      ...localModifierSelections,
      [itemId]: {
        modifierId,
        modifierTextId
      }
    };
    
    setLocalModifierSelections(newSelections);
    
    // Store modifier selection in booking context for later use at checkout
    const modifierData = {
      modifierId,
      modifierTextId,
      modifierName: productModifiers?.find(m => m.id === modifierId)?.productModifierTexts?.find(t => t.id === modifierTextId)?.name || 'Selected Option'
    };
    
    dispatch({
      type: BOOKING_ACTIONS.SET_MODIFIER_SELECTION,
      payload: {
        webSaleItemId: itemId,
        modifierData
      }
    });

  };

  const hasSelectedItems = Object.values(selectedItems).some(qty => qty > 0);

  if (!isOpen) return null;

  return createPortal(
    <ModalOverlay onClick={onClose}>
      <ModalContainer ref={modalContentRef} onClick={(e) => e.stopPropagation()}>
        <ModalHeader>
          <CloseButton onClick={onClose}>×</CloseButton>
          <ModalTitle>{upsell?.name || 'Experience Details'}</ModalTitle>
          {upsell?.description && (
            <ModalDescription>{upsell.description}</ModalDescription>
          )}
        </ModalHeader>

        <ModalContent>
          <StepIndicator>
            <StepDot isActive={currentStep === 'items'} />
            <StepDot isActive={currentStep === 'times'} />
            <StepDot isActive={currentStep === 'questions'} />
            <StepDot isActive={currentStep === 'modifiers'} />
          </StepIndicator>

          {loadingDetails ? (
            <LoadingSpinner>Loading experience details...</LoadingSpinner>
          ) : currentStep === 'items' && itemDetails?.webSaleItems ? (
            <>
              <ItemsGrid>
                {itemDetails.webSaleItems
                  .filter((item) => shouldShowAfternoonTeaProduct(upsell?.name, item.name, selectedDate))
                  .map((item) => {
                  const isSelected = (selectedItems[item.webSaleItemId] || 0) > 0;
                  const selectedQuantity = selectedItems[item.webSaleItemId] || 0;
                  
                  return (
                    <ItemCard 
                      key={item.webSaleItemId}
                      isSelected={isSelected}
                      onClick={() => {
                        if (!isSelected) {
                          incrementQuantity(item.webSaleItemId);
                        }
                      }}
                    >
                      {isSelected && <SelectedBadge>{selectedQuantity}</SelectedBadge>}
                      
                      <ItemName>{item.name}</ItemName>
                      
                      {isAviaryAfternoonTeaUpsell(upsell?.name) &&
                        isGentlemansAfternoonTeaProduct(item.name) && (
                        <FathersDaySpecialNote>Father's Day Special</FathersDaySpecialNote>
                      )}
                      
                      {item.description && (
                        <ItemDescription>{item.description}</ItemDescription>
                      )}
                      
                      <ItemPrice>£{item.price.toFixed(2)}</ItemPrice>

                      <QuantitySelector onClick={(e) => e.stopPropagation()}>
                        <QuantityButton 
                          onClick={() => decrementQuantity(item.webSaleItemId)}
                          disabled={selectedQuantity <= 0}
                        >
                          −
                        </QuantityButton>
                        
                        <QuantityInput
                          type="number"
                          min="0"
                          max="10"
                          value={selectedQuantity}
                          onChange={(e) => handleQuantityInputChange(item.webSaleItemId, e.target.value)}
                        />
                        
                        <QuantityButton 
                          onClick={() => incrementQuantity(item.webSaleItemId)}
                          disabled={selectedQuantity >= 10 || selectedQuantity >= item.maxPurchaseQty}
                        >
                          +
                        </QuantityButton>
                      </QuantitySelector>
                    </ItemCard>
                  );
                })}
              </ItemsGrid>
            </>
          ) : currentStep === 'times' && eventTimes?.events ? (
            <TimeSelectionContainer>
              <ModalTitle>Choose Your Time</ModalTitle>
              <ModalDescription>Select your preferred time for this experience</ModalDescription>
              
              {loadingTimes ? (
                <LoadingSpinner>Loading available times...</LoadingSpinner>
              ) : (
                <TimeGrid>
                  {eventTimes.events
                    .filter((event) => {
                      // Filter out times before entry time
                      if (selectedTimeSlot) {
                        try {
                          const eventTime = new Date(event.startDateTime);
                          const timeString = typeof selectedTimeSlot === 'string' ? selectedTimeSlot : selectedTimeSlot.time;
                          if (timeString) {
                            const [entryHours, entryMinutes] = timeString.split(':').map(Number);
                            const entryTime = new Date(event.startDateTime);
                            entryTime.setHours(entryHours, entryMinutes, 0, 0);
                            if (eventTime < entryTime) {
                              return false; // Don't show times before entry
                            }
                          }
                        } catch (err) {

                        }
                      }
                      return true; // Show this time slot
                    })
                    .map((event) => {
                    const isSelected = selectedEventTime?.eventId === event.eventId;
                    const timeString = new Date(event.startDateTime).toLocaleTimeString('en-GB', {
                      hour: '2-digit',
                      minute: '2-digit'
                    });
                    
                    // Calculate total selected quantity
                    const totalSelectedQuantity = Object.values(selectedItems).reduce((sum, qty) => sum + qty, 0);
                    
                    // Disable if selected quantity exceeds available spots
                    const isDisabled = totalSelectedQuantity > event.quantityAvailable;
                    
                    return (
                      <TimeOption 
                        key={event.eventId}
                        isSelected={isSelected}
                        disabled={isDisabled}
                        onClick={() => !isDisabled && setSelectedEventTime(event)}
                      >
                        <TimeDisplay>{timeString}</TimeDisplay>
                        <TimeDescription>{event.description}</TimeDescription>
                        <TimeAvailability>
                          {event.quantityAvailable} spots available
                          {isDisabled && totalSelectedQuantity > 0 && (
                            <span style={{ display: 'block', color: '#dc3545', fontSize: '0.85rem', marginTop: '0.25rem' }}>
                              (Need {totalSelectedQuantity} spots)
                            </span>
                          )}
                        </TimeAvailability>
                      </TimeOption>
                    );
                  })}
                </TimeGrid>
              )}
            </TimeSelectionContainer>
          ) : currentStep === 'questions' && eventQuestions?.eventQuestions ? (
            <QuestionsContainer>
              <ModalTitle>Additional Information</ModalTitle>
              <ModalDescription>Please answer the following questions for your booking</ModalDescription>
              
              {loadingQuestions ? (
                <LoadingSpinner>Loading questions...</LoadingSpinner>
              ) : (
                <>
                  {eventQuestions.eventQuestions.map((question) => (
                    <QuestionItem key={question.questionId}>
                      <QuestionText>
                        {question.question}
                        {question.isRequired && <RequiredIndicator>*</RequiredIndicator>}
                      </QuestionText>
                      
                      {question.controlType === 'TextBox' ? (
                        <QuestionInput
                          type="text"
                          value={questionAnswers[question.questionId] || ''}
                          onChange={(e) => handleQuestionAnswerChange(question.questionId, e.target.value)}
                          placeholder={`Please enter your answer${question.isRequired ? ' (required)' : ''}`}
                        />
                      ) : (
                        <QuestionTextArea
                          value={questionAnswers[question.questionId] || ''}
                          onChange={(e) => handleQuestionAnswerChange(question.questionId, e.target.value)}
                          placeholder={`Please enter your answer${question.isRequired ? ' (required)' : ''}`}
                        />
                      )}
                    </QuestionItem>
                  ))}
                </>
              )}
            </QuestionsContainer>
          ) : currentStep === 'modifiers' && productModifiers ? (
            <ModifiersContainer>
              <ModalTitle>Select Options</ModalTitle>
              <ModalDescription>Choose your dietary requirements and preferences</ModalDescription>
              
              {loadingModifiers ? (
                <LoadingSpinner>Loading options...</LoadingSpinner>
              ) : (
                <>
                  {productModifiers.map((modifier, index) => (
                    <ModifierGroup key={`${modifier.productID}-${index}`}>
                      <ModifierPrompt>
                        {modifier.prompts}
                        {modifier.minimumSelectionsAllowed > 0 && (
                          <RequiredIndicator>*</RequiredIndicator>
                        )}
                      </ModifierPrompt>
                      
                      <ModifierOptions>
                        {modifier.productModifierTexts.map((modifierText) => {
                          const isSelected = localModifierSelections[modifier.productID]?.modifierTextId === modifierText.id;
                          
                          return (
                            <ModifierOption
                              key={modifierText.id}
                              isSelected={isSelected}
                              onClick={() => handleModifierSelection(modifier.id, modifierText.id, modifier.productID)}
                            >
                              <ModifierName>{modifierText.name}</ModifierName>
                              <ModifierPrice>
                                {modifierText.price > 0 ? `+£${modifierText.price.toFixed(2)}` : 'Included'}
                              </ModifierPrice>
                            </ModifierOption>
                          );
                        })}
                      </ModifierOptions>
                    </ModifierGroup>
                  ))}
                </>
              )}
            </ModifiersContainer>
          ) : (
            <div style={{ textAlign: 'center', padding: '2rem', color: '#666' }}>
              <p>No items available for this experience.</p>
            </div>
          )}
        </ModalContent>

        {itemDetails?.webSaleItems && (
          <ModalFooter>
            {currentStep === 'items' ? (
              <>
                <TotalAmount>
                  {hasSelectedItems ? `Total: £${calculateTotal().toFixed(2)}` : 'Select items to continue'}
                </TotalAmount>
                
                <ActionButtons>
                  <CancelButton onClick={onClose}>
                    Cancel
                  </CancelButton>
                  
                  <AddToBasketButton 
                    onClick={handleContinueToTimes}
                    disabled={!hasSelectedItems || loadingTimes}
                  >
                    {loadingTimes ? 'Loading times...' : 'Continue to Times'}
                  </AddToBasketButton>
                </ActionButtons>
              </>
            ) : currentStep === 'times' ? (
              <>
                <TotalAmount>
                  {selectedEventTime ? `Selected: ${new Date(selectedEventTime.startDateTime).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })}` : 'Select a time to continue'}
                </TotalAmount>
                
                <ActionButtons>
                  <BackButton onClick={() => setCurrentStep('items')}>
                    ← Back to Items
                  </BackButton>
                  
                  <AddToBasketButton 
                    onClick={handleContinueToQuestions}
                    disabled={!selectedEventTime || loadingQuestions}
                  >
                    {loadingQuestions ? 'Loading questions...' : 'Continue to Questions'}
                  </AddToBasketButton>
                </ActionButtons>
              </>
            ) : currentStep === 'questions' ? (
              <>
                <TotalAmount>
                  {Object.keys(questionAnswers).length > 0 ? 'Questions answered' : 'Answer questions to continue'}
                </TotalAmount>
                
                <ActionButtons>
                  <BackButton onClick={() => setCurrentStep('times')}>
                    ← Back to Times
                  </BackButton>
                  
                  <AddToBasketButton 
                    onClick={handleContinueToModifiers}
                    disabled={loadingModifiers}
                  >
                    {loadingModifiers ? 'Loading options...' : 'Continue to Options'}
                  </AddToBasketButton>
                </ActionButtons>
              </>
            ) : currentStep === 'modifiers' ? (
              <>
                <TotalAmount>
                  {Object.keys(modifierSelections).length > 0 ? 'Options selected' : 'Select options to continue'}
                </TotalAmount>
                
                <ActionButtons>
                  <BackButton onClick={() => setCurrentStep('questions')}>
                    ← Back to Questions
                  </BackButton>
                  
                  <AddToBasketButton 
                    onClick={handleAddToBasket}
                    disabled={loading}
                  >
                    {loading ? 'Adding...' : 'Add to Basket'}
                  </AddToBasketButton>
                </ActionButtons>
              </>
            ) : null}
          </ModalFooter>
        )}
      </ModalContainer>
    </ModalOverlay>,
    document.body
  );
};

export default UpsellDetailModal;